/**
 * ModeRail — the single left sidebar for the whole project workspace. It owns the
 * brand, the project menu, and the mode navigation (Build / Inventory / Results /
 * Compare). One nav, no top bar.
 *
 * The project name doubles as a dropdown trigger: the only file actions a user
 * needs — start a new project, open a saved one (browse a file or pick a recent),
 * or download the current one — live in that one menu. There is no separate
 * bottom "File" rail anymore. Result export lives in the Results panel, where the
 * results actually are.
 *
 * These actions are thin wrappers over the same store actions the AI copilot
 * drives through the action bridge (newProject / openProject / saveProject /
 * loadFromIdb), so the UI and the copilot share one pathway.
 *
 * Coach anchor IDs (tab-inventory / tab-analysis) are kept on the matching mode
 * buttons so the walkthrough spotlights still resolve.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useWorkspace } from './WorkspaceContext';
import useSavedProjects from '../../hooks/useSavedProjects';

const isWeb = process.env.REACT_APP_WEB === '1';

// Small stroke icons that match the mode-button visual language.
const ICONS = {
  new: <><path d="M12 5v14M5 12h14" /></>,
  open: <><path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /></>,
  download: <><path d="M12 3v12" /><path d="M7 11l5 5 5-5" /><path d="M5 21h14" /></>,
};

const MODES = [
  {
    id: 'build', label: 'Build',
    icon: (<><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><path d="M17.5 14v7M14 17.5h7" /></>),
  },
  {
    id: 'inventory', label: 'Inventory', coach: 'tab-inventory',
    icon: (<><path d="M3 6h18M3 12h18M3 18h18" /></>),
  },
  {
    id: 'results', label: 'Results', coach: 'tab-analysis',
    icon: (<><path d="M3 3v18h18" /><path d="M7 14l3-3 3 3 4-5" /></>),
  },
  {
    id: 'compare', label: 'Compare',
    icon: (<><path d="M12 3v18" /><path d="M7 7l-4 4 4 4" /><path d="M17 7l4 4-4 4" /></>),
  },
];

function MenuIcon({ name }) {
  return (
    <svg className="rail-menu-ico" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {ICONS[name]}
    </svg>
  );
}

export default function ModeRail() {
  const {
    workspaceMode, goToMode, project,
    handleNewProject, handleOpenProject, handleSaveProject, handleLoadFromIdb,
  } = useWorkspace();

  const [menuOpen, setMenuOpen] = useState(false);
  const { projects: recentList, loading: loadingRecent, refresh: refreshRecent } = useSavedProjects();
  const menuRef = useRef(null);

  // Close the project menu on outside click.
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [menuOpen]);

  const toggleMenu = () => {
    if (!menuOpen) refreshRecent();
    setMenuOpen((v) => !v);
  };

  // Run a file action then close the menu. Handlers may surface their own
  // save/confirm modals; closing first keeps the menu out of the way.
  const runAction = (fn) => async () => {
    setMenuOpen(false);
    if (!fn) return;
    try { await fn(); }
    catch (err) { console.error('[ModeRail] action failed:', err); }
  };

  const pickRecent = async (entry) => {
    setMenuOpen(false);
    if (!entry?.filePath || !handleLoadFromIdb) return;
    try { await handleLoadFromIdb(entry.filePath); }
    catch (err) { console.error('[ModeRail] load failed:', err); }
  };

  const projectName = project?.project_info?.name || 'Untitled project';

  return (
    <nav className="mode-rail">
      <div className="rail-brand">
        <span className="rail-brand-text">LiTeCAB</span>
        {isWeb && <span className="rail-brand-tag">Web</span>}
      </div>

      <div className="rail-project-wrap" ref={menuRef}>
        <button
          className={`rail-project-btn ${menuOpen ? 'active' : ''}`}
          onClick={toggleMenu}
          title="Project menu — new, open, or download"
          aria-haspopup="menu"
          aria-expanded={menuOpen}
        >
          <span className="rail-project-name">{projectName}</span>
          <span className="rail-project-caret" aria-hidden="true">▾</span>
        </button>

        {menuOpen && (
          <div className="rail-project-menu" role="menu">
            <button className="rail-menu-item" role="menuitem" onClick={runAction(handleNewProject)}>
              <MenuIcon name="new" /> New project
            </button>
            <button className="rail-menu-item" role="menuitem" onClick={runAction(handleOpenProject)}>
              <MenuIcon name="open" /> Open project file…
            </button>

            <div className="rail-menu-sep" />
            <div className="rail-menu-head">Recently saved</div>
            {loadingRecent && <div className="rail-menu-empty">Loading…</div>}
            {!loadingRecent && recentList.length === 0 && (
              <div className="rail-menu-empty">No saved projects yet.</div>
            )}
            {!loadingRecent && recentList.map((p) => (
              <button
                key={p.filePath}
                type="button"
                role="menuitem"
                className="rail-menu-item recent"
                onClick={() => pickRecent(p)}
                title={p.lastSaved ? `Saved ${new Date(p.lastSaved).toLocaleString()}` : ''}
              >
                <span className="rail-menu-recent-name">{p.name}</span>
                {p.lastSaved && <span className="rail-menu-recent-date">{new Date(p.lastSaved).toLocaleDateString()}</span>}
              </button>
            ))}

            <div className="rail-menu-sep" />
            <button
              className="rail-menu-item"
              role="menuitem"
              onClick={runAction(handleSaveProject)}
              disabled={!project}
            >
              <MenuIcon name="download" /> {isWeb ? 'Download this project' : 'Save project as…'}
            </button>
          </div>
        )}
      </div>

      <div className="rail-modes">
        {MODES.map((m) => (
          <button
            key={m.id}
            className={`mode-rail-btn ${workspaceMode === m.id ? 'active' : ''}`}
            onClick={() => goToMode(m.id)}
            data-coach={m.coach || undefined}
            title={m.label}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              {m.icon}
            </svg>
            <span>{m.label}</span>
          </button>
        ))}
      </div>

      <div className="rail-spacer" />
    </nav>
  );
}
